/**
 * @file     MoneyField.tsx
 * @purpose  A labelled numeric input for the family 7 tools: a typed field
 *           (dollars, percent or years) with an optional slider under it, so a
 *           figure can be dragged roughly or typed exactly.
 */
'use client';

import { useEffect, useId, useState } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { focusRingSx, hairline, hubColors, hubRadius } from '@/components/hub/hubKitStyles';

type MoneyFieldProps = {
  label: string;
  value: number;
  onChange: (value: number) => void;
  unit?: 'dollar' | 'percent' | 'number';
  slider?: { min: number; max: number; step: number };
  /** Decimals kept when typing (rates use 2). */
  decimals?: number;
  hint?: React.ReactNode;
  /** Secondary read-out shown beside the label, e.g. the dollar figure for a percent. */
  aside?: string;
};

const format = (value: number, decimals: number) =>
  decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString('en-CA');

export function MoneyField({ label, value, onChange, unit = 'dollar', slider, decimals = 0, hint, aside }: MoneyFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const [draft, setDraft] = useState(() => format(value, decimals));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(format(value, decimals));
  }, [decimals, focused, value]);

  const commit = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const parsed = Number(cleaned);
    if (cleaned === '' || !Number.isFinite(parsed)) return;
    onChange(decimals > 0 ? Math.round(parsed * 10 ** decimals) / 10 ** decimals : Math.round(parsed));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
        <Box component="label" htmlFor={id} sx={{ fontSize: 14, fontWeight: 500, color: hubColors.textPrimary }}>
          {label}
        </Box>
        {aside ? (
          <Box component="span" sx={{ fontSize: 13, color: hubColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
            {aside}
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 48,
          px: 1.75,
          border: hairline,
          borderColor: hubColors.borderStrong,
          borderRadius: `${hubRadius.control}px`,
          backgroundColor: hubColors.surfaceInput,
          '&:focus-within': { borderColor: hubColors.interactivePrimary, boxShadow: `0 0 0 1px ${hubColors.interactivePrimary}` },
        }}
      >
        {unit === 'dollar' ? (
          <Box component="span" aria-hidden="true" sx={{ flexShrink: 0, color: hubColors.textMuted, fontSize: 16, lineHeight: 1 }}>$</Box>
        ) : null}
        <Box
          component="input"
          id={id}
          inputMode="decimal"
          autoComplete="off"
          aria-describedby={hint ? hintId : undefined}
          value={draft}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setDraft(format(value, decimals));
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setDraft(event.target.value);
            commit(event.target.value);
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 0,
            background: 'transparent',
            font: 'inherit',
            fontSize: 17,
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
            color: hubColors.textPrimary,
            py: 1,
          }}
        />
        {unit === 'percent' ? (
          <Box component="span" aria-hidden="true" sx={{ flexShrink: 0, color: hubColors.textMuted, fontSize: 16, lineHeight: 1 }}>%</Box>
        ) : null}
      </Box>
      {slider ? (
        <Slider
          value={Math.min(Math.max(value, slider.min), slider.max)}
          min={slider.min}
          max={slider.max}
          step={slider.step}
          onChange={(_, next) => onChange(next as number)}
          aria-label={label}
          size="small"
          sx={{
            mx: 0.75,
            width: 'calc(100% - 12px)',
            color: hubColors.interactivePrimary,
            '& .MuiSlider-thumb': { width: 20, height: 20, ...focusRingSx },
            '& .MuiSlider-rail': { opacity: 0.25 },
          }}
        />
      ) : null}
      {hint ? (
        <Box id={hintId} component="p" sx={{ m: 0, fontSize: 13, lineHeight: 1.5, color: hubColors.textSecondary }}>
          {hint}
        </Box>
      ) : null}
    </Box>
  );
}
